import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { 
  Book, Droplets, Eye, Heart, Smile, PenTool,
  Target, Zap, Award, TrendingUp, Flame, Star,
  CheckCircle, Circle, Brain, Dumbbell, Music,
  Globe, Code, Palette, Camera, Mic, Languages,
  TreePine, Sun, Moon, Wind, Sparkles, Trophy,
  Coffee, Apple, Footprints, Headphones, BookOpen,
  MessageCircle, Users, Gift, DollarSign, Calculator
} from 'lucide-react';

interface HabitButton {
  id: string;
  label: string;
  icon: React.ReactNode;
  color: string;
  description: string;
  category: 'health' | 'learning' | 'mindfulness' | 'productivity' | 'social' | 'finance';
  points: number;
  streak: number;
  lastCompleted?: string;
  timeEstimate?: string;
  motivationalQuote?: string;
}

interface HabitButtonsProps {
  onComplete?: (habit: HabitButton) => void;
}

export function HabitButtons({ onComplete }: HabitButtonsProps) {
  const [habits, setHabits] = useState<HabitButton[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [dailyScore, setDailyScore] = useState(0);

  // Load saved habit data
  useEffect(() => {
    const savedHabits = localStorage.getItem('workbuttons_habits');
    if (savedHabits) {
      setHabits(JSON.parse(savedHabits));
    } else {
      setHabits(defaultHabits);
    }
  }, []);

  const handleHabitClick = (habit: HabitButton) => {
    const now = new Date().toISOString();
    const today = new Date().toDateString();
    const lastCompleted = habit.lastCompleted ? new Date(habit.lastCompleted).toDateString() : null;
    
    // Check if already completed today
    if (lastCompleted === today) {
      toast('Already completed today! Great job! 🌟', { icon: '✅' });
      return;
    }

    // Update habit
    const updatedHabits = habits.map(h => {
      if (h.id === habit.id) {
        const wasYesterday = lastCompleted === new Date(Date.now() - 86400000).toDateString();
        const newStreak = wasYesterday ? h.streak + 1 : 1;
        
        return {
          ...h,
          lastCompleted: now,
          streak: newStreak
        };
      }
      return h;
    });

    setHabits(updatedHabits);
    localStorage.setItem('workbuttons_habits', JSON.stringify(updatedHabits));
    
    // Update daily score
    setDailyScore(prev => prev + habit.points);
    
    // Show motivational message
    const messages = getMotivationalMessage(habit);
    toast.success(messages.completion, {
      duration: 4000,
      icon: messages.icon
    });

    // Trigger celebration for milestones
    if (habit.streak > 0 && (habit.streak + 1) % 7 === 0) {
      setTimeout(() => {
        toast(`🎉 ${habit.streak + 1} day streak! You're unstoppable!`, {
          duration: 5000,
          style: {
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
          }
        });
      }, 1000);
    }

    onComplete?.(habit);
  };

  const getMotivationalMessage = (habit: HabitButton) => {
    const messages = {
      health: {
        completion: `${habit.label} complete! Your body thanks you! 💪`,
        icon: '🏃'
      },
      learning: {
        completion: `Knowledge gained! +${habit.points} brain points! 🧠`,
        icon: '📚'
      },
      mindfulness: {
        completion: `Inner peace +${habit.points}. You're glowing! ✨`,
        icon: '🧘'
      },
      productivity: {
        completion: `Productivity boost! You're on fire! 🔥`,
        icon: '🚀'
      },
      social: {
        completion: `Connection made! The world is brighter! 🌟`,
        icon: '💫'
      },
      finance: {
        completion: `Smart money move! Future you says thanks! 💰`,
        icon: '📈'
      }
    };
    
    return messages[habit.category];
  };

  const categories = [
    { id: 'all', label: 'All', icon: <Sparkles size={16} /> },
    { id: 'health', label: 'Health', icon: <Heart size={16} /> },
    { id: 'learning', label: 'Learning', icon: <Brain size={16} /> },
    { id: 'mindfulness', label: 'Mindfulness', icon: <Sun size={16} /> },
    { id: 'productivity', label: 'Productivity', icon: <Zap size={16} /> },
    { id: 'social', label: 'Social', icon: <Users size={16} /> },
    { id: 'finance', label: 'Finance', icon: <DollarSign size={16} /> }
  ];

  const filteredHabits = selectedCategory === 'all' 
    ? habits 
    : habits.filter(h => h.category === selectedCategory);

  const todayCompleted = habits.filter(h => {
    if (!h.lastCompleted) return false;
    return new Date(h.lastCompleted).toDateString() === new Date().toDateString();
  }).length;

  return (
    <div className="space-y-6">
      {/* Daily Progress Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl p-6 text-white"
      >
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-2xl font-bold mb-2">Daily Habits</h3>
            <p className="opacity-90">Small steps, big changes</p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold">{todayCompleted}/{habits.length}</div>
            <div className="text-sm opacity-90">Completed Today</div>
            <div className="mt-2 flex items-center gap-2">
              <Flame className="text-yellow-300" size={20} />
              <span className="font-semibold">{dailyScore} points</span>
            </div>
          </div>
        </div>
        
        {/* Progress bar */}
        <div className="mt-4 bg-white/20 rounded-full h-3 overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${(todayCompleted / habits.length) * 100}%` }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="h-full bg-white rounded-full"
          />
        </div>
      </motion.div>

      {/* Category Filter */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {categories.map(cat => (
          <button
            key={cat.id}
            onClick={() => setSelectedCategory(cat.id)}
            className={`
              flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-all
              ${selectedCategory === cat.id 
                ? 'bg-blue-500 text-white shadow-lg' 
                : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'}
            `}
          >
            {cat.icon}
            <span className="text-sm font-medium">{cat.label}</span>
          </button>
        ))}
      </div>

      {/* Habit Buttons Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <AnimatePresence mode="popLayout">
          {filteredHabits.map((habit, index) => (
            <HabitButtonCard
              key={habit.id}
              habit={habit}
              onClick={() => handleHabitClick(habit)}
              delay={index * 0.05}
            />
          ))}
        </AnimatePresence>
      </div>

      {/* Motivational Quote */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="text-center p-6 bg-gray-50 dark:bg-gray-800 rounded-lg"
      >
        <p className="text-gray-600 dark:text-gray-400 italic">
          "We are what we repeatedly do. Excellence, then, is not an act, but a habit."
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">- Aristotle</p>
      </motion.div>
    </div>
  );
}

interface HabitButtonCardProps {
  habit: HabitButton;
  onClick: () => void;
  delay: number;
}

function HabitButtonCard({ habit, onClick, delay }: HabitButtonCardProps) {
  const isCompletedToday = habit.lastCompleted && 
    new Date(habit.lastCompleted).toDateString() === new Date().toDateString();

  return (
    <motion.button
      layout
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
      transition={{ delay }}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={onClick}
      className={`
        relative p-4 rounded-xl shadow-lg transition-all
        ${isCompletedToday 
          ? 'bg-gradient-to-br from-green-400 to-green-600 text-white' 
          : `bg-gradient-to-br ${habit.color} text-white hover:shadow-xl`}
      `}
    >
      {/* Completion badge */}
      {isCompletedToday && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="absolute -top-2 -right-2 bg-white rounded-full p-1 shadow-md"
        >
          <CheckCircle className="text-green-500" size={20} />
        </motion.div>
      )}

      {/* Streak indicator */}
      {habit.streak > 0 && (
        <div className="absolute top-2 left-2 bg-white/20 backdrop-blur rounded-full px-2 py-1 flex items-center gap-1">
          <Flame size={12} />
          <span className="text-xs font-bold">{habit.streak}</span>
        </div>
      )}

      <div className="flex flex-col items-center gap-2 mt-2">
        <motion.div
          animate={isCompletedToday ? { rotate: 360 } : {}}
          transition={{ duration: 0.5 }}
        >
          {habit.icon}
        </motion.div>
        <span className="text-sm font-medium">{habit.label}</span>
        {habit.timeEstimate && (
          <span className="text-xs opacity-80">{habit.timeEstimate}</span>
        )}
        <div className="flex items-center gap-1 mt-1">
          <Star size={12} />
          <span className="text-xs">+{habit.points}</span>
        </div>
      </div>
    </motion.button>
  );
}

// Default habit buttons
const defaultHabits: HabitButton[] = [
  // Health
  {
    id: 'water',
    label: 'Drink Water',
    icon: <Droplets size={20} />,
    color: 'from-blue-400 to-cyan-600',
    description: 'Stay hydrated',
    category: 'health',
    points: 5,
    streak: 0,
    timeEstimate: '30 sec'
  },
  {
    id: 'stretch',
    label: 'Quick Stretch',
    icon: <Dumbbell size={20} />,
    color: 'from-purple-400 to-pink-600',
    description: '2-minute desk stretch',
    category: 'health',
    points: 10,
    streak: 0,
    timeEstimate: '2 min'
  },
  {
    id: 'eyes',
    label: '20-20-20 Rule',
    icon: <Eye size={20} />,
    color: 'from-green-400 to-teal-600',
    description: 'Rest your eyes',
    category: 'health',
    points: 5,
    streak: 0,
    timeEstimate: '20 sec'
  },
  {
    id: 'walk',
    label: 'Take 100 Steps',
    icon: <Footprints size={20} />,
    color: 'from-orange-400 to-red-600',
    description: 'Quick movement break',
    category: 'health',
    points: 15,
    streak: 0,
    timeEstimate: '1 min'
  },
  {
    id: 'posture',
    label: 'Fix Posture',
    icon: <Heart size={20} />,
    color: 'from-pink-400 to-rose-600',
    description: 'Sit up straight',
    category: 'health',
    points: 5,
    streak: 0,
    timeEstimate: '5 sec'
  },

  // Learning
  {
    id: 'read',
    label: 'Read 1 Page',
    icon: <BookOpen size={20} />,
    color: 'from-indigo-400 to-blue-600',
    description: 'Learn something new',
    category: 'learning',
    points: 20,
    streak: 0,
    timeEstimate: '2 min'
  },
  {
    id: 'vocab',
    label: 'Learn 1 Word',
    icon: <Languages size={20} />,
    color: 'from-yellow-400 to-orange-600',
    description: 'Expand vocabulary',
    category: 'learning',
    points: 15,
    streak: 0,
    timeEstimate: '1 min'
  },
  {
    id: 'code',
    label: 'Code Snippet',
    icon: <Code size={20} />,
    color: 'from-gray-600 to-gray-800',
    description: 'Practice coding',
    category: 'learning',
    points: 25,
    streak: 0,
    timeEstimate: '5 min'
  },
  {
    id: 'trivia',
    label: 'Learn Fun Fact',
    icon: <Brain size={20} />,
    color: 'from-purple-400 to-indigo-600',
    description: 'Discover something cool',
    category: 'learning',
    points: 10,
    streak: 0,
    timeEstimate: '30 sec'
  },

  // Mindfulness
  {
    id: 'breathe',
    label: 'Deep Breath',
    icon: <Wind size={20} />,
    color: 'from-cyan-400 to-blue-600',
    description: '3 deep breaths',
    category: 'mindfulness',
    points: 10,
    streak: 0,
    timeEstimate: '30 sec'
  },
  {
    id: 'gratitude',
    label: 'Gratitude',
    icon: <Heart size={20} />,
    color: 'from-pink-400 to-purple-600',
    description: 'Think of 1 thing',
    category: 'mindfulness',
    points: 15,
    streak: 0,
    timeEstimate: '1 min'
  },
  {
    id: 'smile',
    label: 'Smile',
    icon: <Smile size={20} />,
    color: 'from-yellow-400 to-yellow-600',
    description: 'Boost mood instantly',
    category: 'mindfulness',
    points: 5,
    streak: 0,
    timeEstimate: '5 sec'
  },
  {
    id: 'nature',
    label: 'Look Outside',
    icon: <TreePine size={20} />,
    color: 'from-green-400 to-green-600',
    description: 'Connect with nature',
    category: 'mindfulness',
    points: 10,
    streak: 0,
    timeEstimate: '30 sec'
  },

  // Productivity
  {
    id: 'inbox-zero',
    label: 'Clear 1 Email',
    icon: <MessageCircle size={20} />,
    color: 'from-red-400 to-pink-600',
    description: 'Inbox progress',
    category: 'productivity',
    points: 10,
    streak: 0,
    timeEstimate: '1 min'
  },
  {
    id: 'organize',
    label: 'Tidy Desk',
    icon: <Target size={20} />,
    color: 'from-blue-400 to-indigo-600',
    description: 'Clean workspace',
    category: 'productivity',
    points: 15,
    streak: 0,
    timeEstimate: '2 min'
  },
  {
    id: 'journal',
    label: 'Quick Note',
    icon: <PenTool size={20} />,
    color: 'from-purple-400 to-purple-600',
    description: 'Capture a thought',
    category: 'productivity',
    points: 10,
    streak: 0,
    timeEstimate: '30 sec'
  },
  {
    id: 'plan',
    label: 'Plan 1 Task',
    icon: <CheckCircle size={20} />,
    color: 'from-green-400 to-emerald-600',
    description: 'Set intention',
    category: 'productivity',
    points: 15,
    streak: 0,
    timeEstimate: '1 min'
  },

  // Social
  {
    id: 'compliment',
    label: 'Give Compliment',
    icon: <Award size={20} />,
    color: 'from-yellow-400 to-orange-600',
    description: 'Brighten someone\'s day',
    category: 'social',
    points: 20,
    streak: 0,
    timeEstimate: '30 sec'
  },
  {
    id: 'connect',
    label: 'Text a Friend',
    icon: <Users size={20} />,
    color: 'from-blue-400 to-purple-600',
    description: 'Stay connected',
    category: 'social',
    points: 15,
    streak: 0,
    timeEstimate: '1 min'
  },
  {
    id: 'share',
    label: 'Share Knowledge',
    icon: <Gift size={20} />,
    color: 'from-green-400 to-teal-600',
    description: 'Help someone',
    category: 'social',
    points: 25,
    streak: 0,
    timeEstimate: '2 min'
  },

  // Finance
  {
    id: 'save',
    label: 'Save $1',
    icon: <DollarSign size={20} />,
    color: 'from-green-400 to-green-600',
    description: 'Future you thanks you',
    category: 'finance',
    points: 20,
    streak: 0,
    timeEstimate: '30 sec'
  },
  {
    id: 'track',
    label: 'Log Expense',
    icon: <Calculator size={20} />,
    color: 'from-blue-400 to-blue-600',
    description: 'Track spending',
    category: 'finance',
    points: 10,
    streak: 0,
    timeEstimate: '30 sec'
  }
];